// pages/SubstanceLibraryPage/SubstanceLibraryPage.tsx
//
// The Substance Library page at /ingredients.
// Renders inside the ProtectedShell <Outlet/> — the left Sidebar is provided
// by the shell and MUST stay visible.  No standalone N-OS logo/back bar.
//
// Data: useInventoryLibraryItems (keyset infinite-scroll, sorted by code asc).
// LibraryItem[] pages are flattened → SubstanceRow[] client-side via
// substanceRowsFromLibraryItems.  A local `subs` state mirrors the server
// data so drawer edits update the UI optimistically.
//
// B3: drawer writes are persisted via useSubstancePersistence (diff-based) —
// the view keeps the optimistic `subs` mirror AND routes deltas to the real
// inventory mutations.  Add-mode CODE suggestion is debounced through
// onResolveCode → resolveLibraryItem; the suggested code lands via a list
// refetch after create.

import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useInventoryLibraryItems } from "@/hooks/useInventoryLibraryItems";
import { useInventoryCatalog } from "@/hooks/useInventoryCatalog";
import { useInventorySuppliers } from "@/hooks/useInventorySupplier";
import { substanceRowsFromLibraryItems } from "./substance/fromLibraryItem";
import { useSubstancePersistence } from "./substance/useSubstancePersistence";
import type { SubstanceRow } from "./substance/_model";
import { SubstanceLibraryView } from "./substance/SubstanceLibraryView";
import type { DrawerCatalogs } from "./substance/drawer/SlSubstanceDrawer";
import {
  adaptCustomSuggestions,
  adaptFlavorFamilies,
  adaptLocations,
  adaptTypeRows,
} from "./SidebarPanel/catalogAdapters";
import type { ComboOption } from "./SidebarPanel/_types";
import { resolveLibraryItem } from "@/services/inventory_api";
import type { LibraryItem } from "@/services/inventory_types";

export function SubstanceLibraryPage() {
  const qc = useQueryClient();

  // Server query — sort by code asc, no server-side filters (all client-side for B2).
  const libraryQuery = useInventoryLibraryItems({
    sort: "code",
    dir: "asc",
    filters: {},
  });

  // Flatten infinite pages → LibraryItem[]
  const libraryItems = React.useMemo<LibraryItem[]>(
    () => libraryQuery.data?.pages.flatMap((p) => p.items) ?? [],
    [libraryQuery.data],
  );

  // Adapt LibraryItem[] → SubstanceRow[]
  const liveSubs = React.useMemo(
    () => substanceRowsFromLibraryItems(libraryItems),
    [libraryItems],
  );

  // Local mirror so drawer edits are optimistic. Synced from liveSubs whenever
  // the server data changes — but we guard against clobbering an in-flight edit:
  // while the drawer is editing a row, a background refetch's version of THAT
  // row is dropped in favour of the local (optimistic) one, then reconciles
  // once the edit window closes. `editingIdRef` tracks the open row.
  const [subs, setSubs] = React.useState<SubstanceRow[]>([]);
  const editingIdRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    setSubs((prev) => {
      const editId = editingIdRef.current;
      if (!editId) return liveSubs;
      // Preserve the locally-edited row; take the server version for the rest.
      const localEdited = prev.find((s) => s.id === editId);
      if (!localEdited) return liveSubs;
      const merged = liveSubs.map((s) => (s.id === editId ? localEdited : s));
      // If the edited row is a freshly-created temp row not yet in liveSubs,
      // keep it at the front so the drawer doesn't lose it.
      if (!merged.some((s) => s.id === editId)) return [localEdited, ...merged];
      return merged;
    });
  }, [liveSubs]);

  const persistence = useSubstancePersistence(libraryItems);

  // ── Live catalogs for the drawer's pickers (mirrors InventoryItemPanel) ─────
  // These hooks live HERE (in the page), never inside the drawer/view — so the
  // DEV route, which renders the view without these props, fires no network.
  const currencyQ = useInventoryCatalog("currency");
  const unitQ = useInventoryCatalog("unit");
  const storageQ = useInventoryCatalog("storage-condition");
  const formQ = useInventoryCatalog("form");
  const standardQ = useInventoryCatalog("standard");
  const claimQ = useInventoryCatalog("claim");
  const familyQ = useInventoryCatalog("flavor-family");
  const locationQ = useInventoryCatalog("location");
  const customQ = useInventoryCatalog("custom");
  const suppliersQ = useInventorySuppliers();

  const drawerCatalogs = React.useMemo<DrawerCatalogs>(
    () => ({
      units: { items: adaptTypeRows(unitQ.data?.items), raws: unitQ.data?.raws ?? [] },
      currencies: {
        items: adaptTypeRows(currencyQ.data?.items),
        raws: currencyQ.data?.raws ?? [],
      },
      storageConditions: {
        items: adaptTypeRows(storageQ.data?.items),
        raws: storageQ.data?.raws ?? [],
      },
      forms: { items: adaptTypeRows(formQ.data?.items), raws: formQ.data?.raws ?? [] },
      standards: {
        items: adaptTypeRows(standardQ.data?.items),
        raws: standardQ.data?.raws ?? [],
      },
      claims: { items: adaptTypeRows(claimQ.data?.items), raws: claimQ.data?.raws ?? [] },
      flavorFamilies: adaptFlavorFamilies(familyQ.data?.items),
      locations: adaptLocations(locationQ.data?.items),
      customSuggestions: adaptCustomSuggestions(customQ.data?.items),
      suppliers: deriveSupplierOptions(suppliersQ.data?.items),
      // Library codes from the loaded library items; catalog nos from the
      // distinct supplier_catalog_no values across all loaded inventory lots.
      libraryCodes: deriveLibraryCodeOptions(libraryItems),
      catalogNos: deriveCatalogNoOptions(libraryItems),
    }),
    [
      unitQ.data,
      currencyQ.data,
      storageQ.data,
      formQ.data,
      standardQ.data,
      claimQ.data,
      familyQ.data,
      locationQ.data,
      customQ.data,
      suppliersQ.data,
      libraryItems,
    ],
  );

  // Add-mode CODE suggestion: resolve identifiers → CODE, then warm the list so
  // the suggestion is visible. (The drawer's add row is local; the resolved code
  // is surfaced on the next create's response / list refetch.)
  const onResolveCode = React.useCallback(
    (identifiers: Array<{ kind: string; value: string }>) => {
      const idents = identifiers
        .map((i) => ({ kind: String(i.kind).toLowerCase(), value: i.value }))
        .filter((i) => i.value.trim() !== "");
      if (idents.length === 0) return;
      void resolveLibraryItem({ identifiers: idents }).then((r) => {
        // matched-existing means this lot already exists under a CODE; new-code
        // gives the auto-assigned CODE. Either way, refresh the list so any
        // grouping reflects the resolution.
        if (r.resolution === "matched-existing") {
          void qc.invalidateQueries({ queryKey: ["inventory", "library-items"] });
        }
      });
    },
    [qc],
  );

  return (
    <div
      className="fade-in"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        background: "var(--bg)",
        minWidth: 760,
      }}
    >
      <SubstanceLibraryView
        subs={subs}
        setSubs={setSubs}
        // "Upload inventory" is deferred (Phase B deferred list); disable with tooltip.
        // onUploadInventory is intentionally omitted → button renders disabled.
        fetchNextPage={libraryQuery.fetchNextPage}
        hasNextPage={libraryQuery.hasNextPage}
        isFetchingNextPage={libraryQuery.isFetchingNextPage}
        onPersistUpdate={persistence.onPersistUpdate}
        onPersistCreate={persistence.onPersistCreate}
        onPersistDelete={persistence.onPersistDelete}
        onResolveCode={onResolveCode}
        drawerCatalogs={drawerCatalogs}
        // Drives the in-flight-edit guard on the optimistic `subs` mirror.
        onEditingChange={(id) => {
          editingIdRef.current = id;
        }}
      />
    </div>
  );
}

// ── Combo-option derivations (flat lists the drawer's ComboFields consume) ────

/** Supplier ComboOptions from the live /me/inventory/suppliers response. The
 *  drawer stores the supplier NAME; the persistence hook resolves name→id. */
function deriveSupplierOptions(
  items: { id: number; name: string; description: string | null }[] | undefined,
): ComboOption[] {
  if (!items || items.length === 0) return [];
  return items.map((s) => ({
    value: s.name,
    label: s.name,
    ...(s.description ? { hint: s.description } : {}),
  }));
}

/** Library-code ComboOptions derived from the loaded library items' codes. */
function deriveLibraryCodeOptions(items: LibraryItem[]): ComboOption[] {
  const seen = new Map<string, ComboOption>();
  for (const li of items) {
    if (li.code && !seen.has(li.code)) {
      seen.set(li.code, { value: li.code, label: li.code, hint: li.name });
    }
  }
  return [...seen.values()];
}

/** Catalog-no ComboOptions: distinct supplier_catalog_no across all loaded
 *  inventory lots, hinted with the supplier name. (Supplier-keyed narrowing via
 *  useInventorySupplierCatalogNos is deferred — the drawer's selected supplier
 *  is local to the drawer, so a page-level free combo is the pragmatic source.) */
function deriveCatalogNoOptions(items: LibraryItem[]): ComboOption[] {
  const seen = new Map<string, ComboOption>();
  for (const li of items) {
    for (const inv of li.inventory) {
      const cat = inv.supplierCatalogNo;
      if (!cat || seen.has(cat)) continue;
      const supName =
        inv.supplierName ?? (inv.supplierId ? `Supplier #${inv.supplierId}` : "");
      seen.set(cat, { value: cat, label: cat, ...(supName ? { hint: supName } : {}) });
    }
  }
  return [...seen.values()];
}
